import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ConversionResult, SourceDefinitionMetadata, Definition, DefinitionVersion } from '@nx-ahs/formly/shared';

// Import Tripetto interfaces
import { TripettoDefinition, TripettoNode } from './utils/tripetto-interfaces';

// Import configuration
import { BLOCK_TYPE_MAPPING } from './config/block-type-mapping';

// Import converters
import { convertAllNodesToFields, applyBranchConditions } from './converters/two-pass-cluster-converter';

// Import utilities
import { calculateStatistics } from './utils/statistics-calculator';
import { generateWarning } from './utils/warning-generators';
import { NodeToFieldMap } from './utils/node-field-map';

/**
 * Service to convert Tripetto script definitions to Formly configurations
 */
@Injectable({
	providedIn: 'root'
})
export class TripettoToFormlyConverterService {
	private baseApiUrl: string;

	constructor(
		private http: HttpClient,
		@Inject('environment') private environment: any
	) {
		this.baseApiUrl = this.environment.apiUrl;
	}

	/**
	 * Convert a Tripetto definition JSON string to Formly configuration
	 * @param definitionData JSON string of Tripetto IDefinition
	 * @param metadata Optional metadata about the source definition
	 * @returns ConversionResult with Formly config and warnings
	 */
	public convert(definitionData: string, metadata?: SourceDefinitionMetadata): ConversionResult {
		const warnings: any[] = [];
		const formlyFields: any[] = [];
		const unconverted: TripettoNode[] = [];

		let definition: TripettoDefinition;

		// Step 1: Parse JSON
		try {
			definition = JSON.parse(definitionData);
		} catch (error) {
			warnings.push(
				generateWarning('error', '', 'Root', '', `Failed to parse definition JSON: ${error.message}`)
			);
			return {
				formlyConfig: [],
				warnings,
				statistics: calculateStatistics([], warnings, unconverted, 0, 0)
			};
		}

		// Step 2: Two-pass conversion
		const nodeFieldMap = new NodeToFieldMap();

		// PASS 1: Convert all nodes to Formly fields and register in map
		const pass1Result = convertAllNodesToFields(definition.clusters || [], BLOCK_TYPE_MAPPING, nodeFieldMap);
		formlyFields.push(...pass1Result.fields);
		warnings.push(...pass1Result.warnings);
		unconverted.push(...pass1Result.unconverted);

		// PASS 2: Apply branch conditions as hideExpression using actual Formly field data
		const pass2Warnings = applyBranchConditions(definition.clusters || [], nodeFieldMap);
		warnings.push(...pass2Warnings);

		// Count nodes and branches for statistics
		let totalNodes = 0;
		let totalBranches = 0;

		for (const cluster of definition.clusters || []) {
			totalNodes += (cluster.nodes || []).length;
			totalBranches += (cluster.branches || []).length;

			// Count branches in nodes too
			for (const node of cluster.nodes || []) {
				if (node.branches && node.branches.length > 0) {
					totalBranches += node.branches.length;
				}
			}
		}

		// Step 3: Calculate statistics
		const statistics = calculateStatistics(formlyFields, warnings, unconverted, totalNodes, totalBranches);

		return {
			formlyConfig: formlyFields,
			warnings,
			statistics,
			sourceDefinition: metadata
		};
	}

	/**
	 * Get all Tripetto Definitions from the SCRIPT.Definition table
	 * @returns Observable of Definition array
	 */
	public getDefinitions(): Observable<Definition[]> {
		return this.http.get<Definition[]>(`${this.baseApiUrl}/api/Contract/GetDefinitions`);
	}

	/**
	 * Get all versions for a specific Tripetto Definition
	 * @param definitionId The ID of the definition to get versions for
	 * @returns Observable of DefinitionVersion array
	 */
	public getDefinitionVersions(definitionId: number): Observable<DefinitionVersion[]> {
		return this.http.get<DefinitionVersion[]>(
			`${this.baseApiUrl}/api/Contract/GetDefinitionVersionsByDefinitionIds`,
			{
				params: { definitionId: definitionId.toString() }
			}
		);
	}
}
